import { useState } from "react";
import { useIntegrations } from "../lib/integrations";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { ago } from "../lib/format";
import { ErrorText } from "./ui";

interface Conn {
  id: string;
  member_id: string;
  member_name: string;
  provider: "google" | "microsoft";
  account_email: string;
  status: "active" | "reconnect";
  block_busy: boolean;
  push_bookings: boolean;
  last_sync_at: string | null;
  last_error: string | null;
  busy_ahead?: number;
  mine: boolean;
  off: boolean;
  covers?: string[];
  linked?: string[];
}

type SyncChoice = "both" | "into_svenex" | "to_calendar" | "off";
interface ConnList {
  connections: Conn[]; available: Record<string, boolean>; can_manage: boolean; is_bookable: boolean;
  sync_choices?: Record<string, string>; resources?: { id: string; name: string; mine: boolean }[];
}

const choiceLabel: Record<SyncChoice, string> = {
  both: "Both ways",
  into_svenex: "Only into SVENEX",
  to_calendar: "Only out to the calendar",
  off: "Off",
};
const choiceHelp: Record<SyncChoice, string> = {
  both: "Busy times there block bookings here, and SVENEX bookings are added there.",
  into_svenex: "Busy times there block bookings here. Nothing is written to the calendar.",
  to_calendar: "SVENEX bookings are added there. Its busy times don't block bookings here.",
  off: "Connected calendars of this type do nothing.",
};

const names: Record<string, string> = { google: "Google Calendar", microsoft: "Outlook calendar" };

export const resultText: Record<string, string> = {
  connected: "Your calendar is connected. Busy times will show here within a few minutes.",
  reconnected: "Your calendar is connected again.",
  cancelled: "Connecting your calendar was cancelled.",
  expired: "That link expired or was opened in another browser. Try again from here.",
  not_in_plan: "Calendar sync isn't included in your plan.",
  no_offline_access: "The calendar didn't allow SVENEX to keep access. Try again and accept the permission.",
  not_allowed: "You can't connect a calendar for this company.",
  rate_limited: "Too many attempts. Wait a few minutes and try again.",
  failed: "The calendar couldn't be connected. Please try again.",
};

export function useCalendarConnections(base: string) {
  return useQuery({ queryKey: [base, "calendar-connections"], queryFn: () => api<ConnList>(`${base}/calendar/connections`), staleTime: 30_000 });
}

/** CalendarConnections: connect your Google or Outlook calendar, and (for admins) see the team's. */
export function CalendarConnections({ base, onClose }: { base: string; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useCalendarConnections(base);
  const integ = useIntegrations();
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState("");
  const refresh = () => qc.invalidateQueries({ queryKey: [base, "calendar-connections"] });
  async function run(key: string, fn: () => Promise<unknown>) {
    setBusy(key); setError(null);
    try { await fn(); await refresh(); } catch (e) { setError(e); } finally { setBusy(""); }
  }
  const d = q.data;
  const mine = d?.connections.filter((c) => c.mine) ?? [];
  const team = d?.connections.filter((c) => !c.mine) ?? [];
  const offered = Object.entries(d?.available ?? {}).filter(([p, on]) => on && integ.has(p === "google" ? "google_calendar" : "outlook_calendar")).map(([p]) => p);
  return (
    <section className="card cal-connect">
      <div className="inline"><h2 style={{ margin: 0 }}>Calendar sync</h2><span className="spacer" /><button className="secondary" onClick={onClose}>Close</button></div>
      <p className="muted small">Connect your own calendar: times you're busy there can't be booked with you (by the AI, your booking page or the team), and your SVENEX bookings are added to it and kept up to date. Only the start and end of your other events are read, never what they are.</p>
      <ErrorText error={error ?? q.error} />
      {d?.can_manage && d.sync_choices && offered.length > 0 && (
        <div className="cal-choices">
          <h3>How calendars sync for your company</h3>
          {offered.map((p) => {
            const v = (d.sync_choices![p] ?? "both") as SyncChoice;
            return (
              <div key={p} className="cal-choice">
                <label className="inline small"><b>{names[p]}</b>
                  <select value={v} disabled={!!busy} onChange={(e) => run("m" + p, () => api(`${base}/calendar/sync-choices`, { method: "PUT", json: { [p]: e.target.value } }))}>
                    {(Object.keys(choiceLabel) as SyncChoice[]).map((k) => <option key={k} value={k}>{choiceLabel[k]}</option>)}
                  </select>
                </label>
                <span className="muted small">{choiceHelp[v]}</span>
              </div>
            );
          })}
          <div className="cal-choice">
            <label className="inline small"><b>Booking details on calendars</b>
              <select value={d.sync_choices.details ?? "basic"} disabled={!!busy} onChange={(e) => run("mdetails", () => api(`${base}/calendar/sync-choices`, { method: "PUT", json: { details: e.target.value } }))}>
                <option value="basic">Basic: service, staff, customer's first name</option>
                <option value="full">Full: also full name, phone, email and notes</option>
              </select>
            </label>
            <span className="muted small">{(d.sync_choices.details ?? "basic") === "full"
              ? "Anyone who can see these calendars sees your customers' contact details. Every event links back to the booking in SVENEX."
              : "Every event links back to the booking in SVENEX, where the rest is."}</span>
          </div>
        </div>
      )}
      {d && !d.is_bookable && !d.can_manage && <p className="notice small">You aren't set up as someone who can be booked yet. An admin can link you under Services &amp; hours; until then, a connected calendar has no effect.</p>}
      {mine.map((c) => (
        <div key={c.id} className="cal-conn">
          <div>
            <b>{names[c.provider]}</b> <span className="muted small">{c.account_email}</span>
            {c.off && <span className="tag">Off in your plan</span>}
            {c.status === "reconnect" && <span className="tag status-rejected">Needs reconnecting</span>}
            <div className="muted small">
              {c.last_sync_at ? `Checked ${ago(c.last_sync_at)}` : "Checking for the first time…"}
              {c.last_sync_at && c.block_busy && ` · ${c.busy_ahead ?? 0} busy ${c.busy_ahead === 1 ? "time" : "times"} found ahead`}
            </div>
            {c.last_error && c.status === "active" && <p className="notice warn small">The last check failed: {c.last_error}</p>}
            {c.covers && c.covers.length === 0 && (d?.resources?.length ?? 0) > 0 && <p className="notice warn small">This calendar isn't linked to anyone who can be booked, so its busy times don't show on the calendar. Choose who it's for below.</p>}
          </div>
          {d && <CoversPicker conn={c} list={d} disabled={!!busy} onChange={(linked) => run(c.id, () => api(`${base}/calendar/connections/${c.id}`, { method: "PATCH", json: { linked } }))} />}
          {d?.sync_choices?.[c.provider] && d.sync_choices[c.provider] !== "both" && <p className="muted small">Your company set {names[c.provider]} to “{choiceLabel[d.sync_choices[c.provider] as SyncChoice]}”.</p>}
          <label className="inline small"><input type="checkbox" checked={c.block_busy} disabled={!!busy} onChange={(e) => run(c.id, () => api(`${base}/calendar/connections/${c.id}`, { method: "PATCH", json: { block_busy: e.target.checked } }))} /> Busy times block bookings</label>
          <label className="inline small"><input type="checkbox" checked={c.push_bookings} disabled={!!busy} onChange={(e) => run(c.id, () => api(`${base}/calendar/connections/${c.id}`, { method: "PATCH", json: { push_bookings: e.target.checked } }))} /> Add these bookings to this calendar</label>
          <div className="inline">
            {c.status === "reconnect"
              ? <button disabled={!!busy} onClick={() => run("c" + c.provider, async () => { const r = await api<{ url: string }>(`${base}/calendar/connect`, { method: "POST", json: { provider: c.provider } }); window.location.assign(r.url); })}>Reconnect</button>
              : <button className="secondary" disabled={!!busy} onClick={() => run(c.id, () => api(`${base}/calendar/connections/${c.id}/sync`, { method: "POST" }))}>Check now</button>}
            <button className="secondary" disabled={!!busy} onClick={() => run(c.id, () => api(`${base}/calendar/connections/${c.id}`, { method: "DELETE" }))}>Disconnect</button>
          </div>
        </div>
      ))}
      {d && (
        <div className="inline">
          {offered.filter((p) => !mine.some((c) => c.provider === p)).map((p) => (
            <button key={p} disabled={!!busy} onClick={() => run("c" + p, async () => {
              const r = await api<{ url: string }>(`${base}/calendar/connect`, { method: "POST", json: { provider: p } });
              window.location.assign(r.url);
            })}>{busy === "c" + p ? "Opening…" : `Connect ${names[p]}`}</button>
          ))}
          {offered.length === 0 && <p className="muted small">Calendar sync isn't available for your company yet.</p>}
        </div>
      )}
      {d?.can_manage && team.length > 0 && (
        <>
          <h3>Your team</h3>
          <ul className="list">
            {team.map((c) => (
              <li key={c.id} className="row-line">
                <span><b>{c.member_name}</b> · {names[c.provider]} <span className="muted small">{c.account_email}</span>{c.status === "reconnect" && <span className="tag status-rejected">Needs reconnecting</span>}{c.off && <span className="tag">Off in plan</span>}</span>
                <button className="secondary" disabled={!!busy} onClick={() => run(c.id, () => api(`${base}/calendar/connections/${c.id}`, { method: "DELETE" }))}>Disconnect</button>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

/** CoversPicker: whose bookings and availability this calendar is for. */
function CoversPicker({ conn, list, disabled, onChange }: { conn: Conn; list: ConnList; disabled: boolean; onChange: (linked: string[]) => void }) {
  const resources = list.resources ?? [];
  if (resources.length === 0) return null;
  const linked = new Set(conn.linked ?? []);
  const covers = new Set(conn.covers ?? []);
  return (
    <div className="cal-covers">
      <span className="muted small">Bookings for</span>
      <div className="chips cal-cover-chips">
        {resources.map((r) => {
          const own = r.mine; // your own are always included
          const on = own || covers.has(r.id) || linked.has(r.id);
          return (
            <label key={r.id} className="chip small" title={own ? "You: always included" : undefined}>
              <input type="checkbox" checked={on} disabled={disabled || own || !list.can_manage}
                onChange={(e) => { const n = new Set(linked); if (e.target.checked) n.add(r.id); else n.delete(r.id); onChange([...n]); }} />
              {r.name}
            </label>
          );
        })}
      </div>
      {covers.size === 0 && linked.size === 0 && <span className="warn-text small">Pick at least one, or this calendar does nothing.</span>}
    </div>
  );
}
